/**
 * The router's catch-all (`*`). Every path the SPA does not route itself is
 * asked about at GET /api/v1/pages/by-path, because the URL patterns are the
 * server's (admin-editable) — the frontend never parses a tech URL:
 *
 *   tech / university → the page, inside PublicShell
 *   redirect          → history.replace to the canonical path (+ techpage_redirect)
 *   not found         → NotFoundPage (+ techpage_not_found), title "Page Not Found", noindex
 *   429               → a friendly "slow down" message (+ techpage_rate_limited);
 *                       like a 5xx, it leaves the head's title/meta/robots alone
 *
 * `?preview_state=` (the admin panel's preview links) opens the page as that
 * state; the server honours it only for a signed-in admin.
 *
 * The component stays mounted while the visitor moves between pages inside
 * the SPA, so it also records the pageview for those client-side moves
 * (utils/visitorTracking only records the first load by itself).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { NotFoundPage } from '../../../components/NotFoundPage';
import { pageTitle } from '../../../utils/pageTitle';
import { trackPageview } from '../../../utils/visitorTracking';
import { SpinnerIcon } from '../../../components/icons/SpinnerIcon';
import { CosmosLogoIcon } from '../../../components/icons';
import {
  ByPathResult, DEFAULT_TRANSITION, fetchByPath, hasStoredSession, ListParams, PAGE_STATES, PageState,
  PageTransition, refreshSession,
} from './api';
import { PublicShell } from './PublicShell';
import { markShareOpenReported, shareCodeFrom, shareOpenReported } from './shareLinks';
import { useNoIndex } from './seo';
import { TechPage } from './TechPage';
import { UniversityPage } from './UniversityPage';
import { tpClip, tpTrack } from './track';

/** The path the last pageview was recorded for. visitorTracking records the
 *  landing path itself at startup, so that one is not recorded again. */
let lastPageviewPath: string = typeof window !== 'undefined' ? window.location.pathname : '';

/** The page transition from the last page the server sent (every page carries
 *  the admin setting in page.ui.transition), used for the next navigation. */
let lastTransition: PageTransition = DEFAULT_TRANSITION;

/** The site loader over the page being left: the brown arc spinner, the
 *  Cosmos logo with it, or a thin bar along the top. */
const TransitionLoader: React.FC<{ kind: PageTransition['loader'] }> = ({ kind }) => {
  if (kind === 'none') return null;
  if (kind === 'bar') return <div className="tpg-loader-bar" role="status" aria-label="Loading" />;
  return (
    <div className="tpg-loader" role="status" aria-live="polite" aria-label="Loading">
      {kind === 'logo' && <div className="tpg-loader-logo"><CosmosLogoIcon /></div>}
      <SpinnerIcon />
    </div>
  );
};

/** Collapse duplicate slashes and drop a trailing one ("/" stays "/"). */
export const normalisePath = (p: string): string => (p || '/').replace(/\/{2,}/g, '/').replace(/(.)\/+$/, '$1');

/** Page paths are lowercase [a-z0-9-/] (slugs + pattern literals), so
 *  anything else (/favicon.ico, /wp-login.php, …) is not asked about. */
const isCandidate = (p: string): boolean => p.length > 1 && p.length <= 400 && /^\/[a-z0-9\-/]+$/.test(p);

/** ?q=&industry=&trl=&page= (page 1-based in the URL, 0-based for the API). */
const parseList = (search: string): ListParams => {
  const sp = new URLSearchParams(search);
  const out: ListParams = {};
  const q = (sp.get('q') || '').trim().slice(0, 100);
  if (q) out.q = q;
  const ind = (sp.get('industry') || '').trim().slice(0, 200);
  if (ind) out.industry = ind;
  const trl = Number(sp.get('trl'));
  if (Number.isInteger(trl) && trl >= 1 && trl <= 9) out.trl = trl;
  const page = Number(sp.get('page'));
  if (Number.isInteger(page) && page > 1) out.page = Math.min(page - 1, 200);
  return out;
};

/** ?preview_state=pre_login|post_login|paid — the admin panel's "preview as
 *  each state" links. Not a list filter: the server honours it only for a
 *  signed-in admin and ignores it for everyone else. */
const parsePreview = (search: string): PageState | null => {
  const s = new URLSearchParams(search).get('preview_state') as PageState | null;
  return s && PAGE_STATES.includes(s) ? s : null;
};

const listSearch = (l: ListParams): string => {
  const sp = new URLSearchParams();
  if (l.q) sp.set('q', l.q);
  if (l.industry) sp.set('industry', l.industry);
  if (l.trl) sp.set('trl', String(l.trl));
  if (l.page && l.page > 0) sp.set('page', String(l.page + 1));
  const s = sp.toString();
  return s ? `?${s}` : '';
};

const Skeleton: React.FC = () => (
  <div className="tpg-wrap tpg-page" aria-busy="true" aria-label="Loading">
    <span className="tpg-skel" style={{ width: 260, height: 14, marginBottom: 22 }} />
    <span className="tpg-skel" style={{ width: '80%', height: 40, marginBottom: 12 }} />
    <span className="tpg-skel" style={{ width: '45%', height: 40, marginBottom: 22 }} />
    <span className="tpg-skel" style={{ width: 220, height: 16, marginBottom: 26 }} />
    <div style={{ display: 'flex', gap: 12 }}>
      <span className="tpg-skel" style={{ width: 170, height: 46, borderRadius: 32 }} />
      <span className="tpg-skel" style={{ width: 170, height: 46, borderRadius: 32 }} />
    </div>
    <hr className="tpg-divider" />
    <div className="tpg-grid">
      <div>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ marginBottom: 36 }}>
            <span className="tpg-skel" style={{ width: 120, height: 12, marginBottom: 14 }} />
            <span className="tpg-skel" style={{ width: '100%', height: 14, marginBottom: 8 }} />
            <span className="tpg-skel" style={{ width: '94%', height: 14, marginBottom: 8 }} />
            <span className="tpg-skel" style={{ width: '70%', height: 14 }} />
          </div>
        ))}
      </div>
      <div className="tpg-aside">
        {[0, 1].map(i => <span key={i} className="tpg-skel" style={{ width: '100%', height: 120, borderRadius: 12 }} />)}
      </div>
    </div>
  </div>
);

interface Resolved {
  /** The (normalised, lowercase) path this result answers. */
  path: string;
  search: string;
  result: ByPathResult;
}

export const PageResolver: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = normalisePath(location.pathname).toLowerCase();
  const search = location.search;
  const list = useMemo(() => parseList(search), [search]);

  // Admin preview state: the in-page switch, or ?preview_state= from the
  // admin panel's links. A query that carries one (on arrival, or after a
  // navigation to such a link) sets it; a query without one keeps the current
  // choice, so a university-page filter (which rewrites the query) keeps it.
  // Synced during render, not in an effect, so the page is fetched once.
  const urlPreview = parsePreview(search);
  const [preview, setPreview] = useState<PageState | null>(urlPreview);
  const [seenUrlPreview, setSeenUrlPreview] = useState<PageState | null>(urlPreview);
  if (urlPreview !== seenUrlPreview) {
    setSeenUrlPreview(urlPreview);
    if (urlPreview) setPreview(urlPreview);
  }
  const [tick, setTick] = useState(0);
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const [loading, setLoading] = useState(true);
  const redirects = useRef(0);
  const renewed = useRef(false);
  /** Set when this component rewrote the address bar itself (normalising a
   *  resolved path), so the effect does not fetch the same page again. */
  const skipKey = useRef('');
  /** The path the last fetch was for, and when a move to a new one started:
   *  only a change of path (not a filter, a preview switch or a retry) is a
   *  navigation that gets the loader and the entry effect. */
  const fetchedPath = useRef('');
  const navAt = useRef(0);

  // Pageview for client-side arrivals (not the landing load, not a redirect).
  useEffect(() => {
    if (location.pathname === lastPageviewPath) return;
    lastPageviewPath = location.pathname;
    try { trackPageview(location.pathname); } catch { /* analytics never breaks a page */ }
  }, [location.pathname]);
  // Leaving for another route (e.g. back to /universities): coming back to
  // this page later is a new view.
  useEffect(() => () => {
    try { lastPageviewPath = window.location.pathname; } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const key = JSON.stringify([path, search, preview, tick]);
    const isNav = fetchedPath.current !== '' && fetchedPath.current !== path;
    fetchedPath.current = path;
    if (isNav) navAt.current = Date.now();
    if (skipKey.current === key) { skipKey.current = ''; return; }
    if (!isCandidate(path)) {
      tpTrack('techpage_not_found', { path: tpClip(location.pathname, 200) });
      setResolved({ path, search, result: { kind: 'not_found' } });
      setLoading(false);
      return;
    }
    const ctl = new AbortController();
    setLoading(true);
    // ?s=<code>: this visit came through a tracked share link. Passed once per
    // tab (the first answer that is a page), so a reload, a preview switch or
    // a filter change is not counted as another open.
    const code = shareCodeFrom(search);
    let share = code && !shareOpenReported(code) ? code : null;
    (async () => {
      let r = await fetchByPath(path, { previewState: preview, list, signal: ctl.signal, share });
      if (share && (r.kind === 'tech' || r.kind === 'university')) {
        markShareOpenReported(share);
        share = null;
      }
      // A stored session the server did not accept (expired access token):
      // renew it once and ask again, rather than show a signed-in visitor
      // the signed-out page. (An admin previewing pre-login gets admin_preview
      // back, so a valid admin session is never renewed for nothing.)
      if (!ctl.signal.aborted && !renewed.current && hasStoredSession()
          && (r.kind === 'tech' || r.kind === 'university') && r.page.state === 'pre_login'
          && !r.page.admin_preview) {
        renewed.current = true;
        if (await refreshSession() && !ctl.signal.aborted) {
          r = await fetchByPath(path, { previewState: preview, list, signal: ctl.signal });
        }
      }
      if (ctl.signal.aborted) return;

      if (r.kind === 'redirect') {
        const to = r.to || '';
        const toPath = normalisePath(to.split(/[?#]/)[0]);
        if (!to.startsWith('/') || redirects.current >= 3 || toPath.toLowerCase() === path) {
          redirects.current = 0;
          setResolved({ path, search, result: { kind: 'not_found' } });
          setLoading(false);
          return;
        }
        redirects.current += 1;
        tpTrack('techpage_redirect', { from: tpClip(location.pathname, 300), to: tpClip(toPath, 300) });
        // The landing pageview (old path) already maps to this page; the
        // canonical one is not a second view.
        lastPageviewPath = toPath;
        navigate(to.includes('?') ? to : `${toPath}${search}`, { replace: true });
        return;
      }
      redirects.current = 0;
      if (r.kind === 'not_found') tpTrack('techpage_not_found', { path: tpClip(location.pathname, 200) });
      if (r.kind === 'rate_limited') tpTrack('techpage_rate_limited', {});

      // Keep the loader up for at least min_loader_ms after the click, so a
      // cached (instant) answer still reads as moving to a new page.
      if (r.kind === 'tech' || r.kind === 'university') {
        lastTransition = { ...DEFAULT_TRANSITION, ...(r.page.ui?.transition || {}) };
      }
      if (isNav && lastTransition.enabled && lastTransition.loader !== 'none') {
        const wait = lastTransition.min_loader_ms - (Date.now() - navAt.current);
        if (wait > 0) {
          await new Promise(res => setTimeout(res, wait));
          if (ctl.signal.aborted) return;
        }
      }

      let answered = path;
      if ((r.kind === 'tech' || r.kind === 'university') && r.page.path && r.page.path.startsWith('/')) {
        fetchedPath.current = normalisePath(r.page.path).toLowerCase();
        const own = normalisePath(r.page.path);
        if (own !== location.pathname) {
          // Trailing slash or capitals typed by hand: show the page's own URL.
          answered = own.toLowerCase();
          lastPageviewPath = own;
          skipKey.current = JSON.stringify([answered, search, preview, tick]);
          navigate(`${own}${search}`, { replace: true });
        }
      }
      setResolved({ path: answered, search, result: r });
      setLoading(false);
    })().catch(() => { /* aborted */ });
    return () => ctl.abort();
    // `list` is derived from `search`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, search, preview, tick]);

  const onList = useCallback((next: ListParams) => {
    navigate({ pathname: location.pathname, search: listSearch(next) }, { replace: true });
  }, [navigate, location.pathname]);

  const current = resolved && resolved.path === path ? resolved : null;
  const r = current?.result;
  const notFound = !loading && r?.kind === 'not_found';
  // noindex only for a real not-found. A 429 or 5xx is transient (and can hit
  // a crawler's renderer): the server-rendered title/meta/robots stay as they
  // are while the retry message shows.
  useNoIndex(r?.kind === 'not_found');

  useEffect(() => {
    if (notFound) document.title = pageTitle('Page Not Found');
  }, [notFound]);

  // Scroll to the top when a different page (not a filter) comes in.
  const shownPath = current?.path || '';
  useEffect(() => {
    if (!shownPath) return;
    try { window.scrollTo({ top: 0 }); } catch { /* ignore */ }
  }, [shownPath]);

  if (notFound) return <NotFoundPage />;

  const page = r && (r.kind === 'tech' || r.kind === 'university') ? r.page : null;
  const signedIn = hasStoredSession() && (!page || page.state !== 'pre_login' || !!page.admin_preview);
  const outbound = (host: string) => {
    if (r?.kind === 'tech') tpTrack('techpage_outbound', { host, tech_id: r.page.id });
    else if (r?.kind === 'university') tpTrack('unipage_outbound', { host, code: r.page.code });
  };

  const prev = resolved && resolved.path !== path ? resolved.result : null;
  const navigating = !r && !!prev && (prev.kind === 'tech' || prev.kind === 'university') && lastTransition.enabled;

  let body: React.ReactNode;
  if (navigating && prev) {
    body = (
      <>
        <div className="tpg-leaving" aria-hidden="true">
          {prev.kind === 'tech'
            ? <TechPage page={prev.page} busy onPreview={() => { /* leaving */ }} />
            : prev.kind === 'university'
              ? <UniversityPage page={prev.page} busy list={list} onList={() => { /* leaving */ }} onPreview={() => { /* leaving */ }} />
              : null}
        </div>
        <TransitionLoader kind={lastTransition.loader} />
      </>
    );
  } else if (!r) {
    body = <Skeleton />;
  } else if (r.kind === 'tech') {
    body = <TechPage page={r.page} busy={loading} onPreview={s => setPreview(s)} />;
  } else if (r.kind === 'university') {
    body = (
      <UniversityPage
        page={r.page}
        busy={loading}
        list={list}
        onList={onList}
        onPreview={s => setPreview(s)}
      />
    );
  } else if (r.kind === 'rate_limited') {
    body = (
      <div className="tpg-wrap">
        <div className="tpg-state" role="alert">
          <h1>You&apos;re browsing faster than we can keep up</h1>
          <p>We limit how many pages can be opened in a short time to keep the catalogue fair for everyone. Please wait a minute and try again{hasStoredSession() ? '' : ' — or sign in, which raises the limit'}.</p>
          <button type="button" className="tpg-btn-primary" disabled={loading} onClick={() => setTick(t => t + 1)}>Try again</button>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="tpg-wrap">
        <div className="tpg-state" role="alert">
          <h1>This page didn&apos;t load</h1>
          <p>{r.kind === 'error' && r.status === 0 ? r.message : 'Something went wrong on our side. Please try again in a moment.'}</p>
          <button type="button" className="tpg-btn-primary" disabled={loading} onClick={() => setTick(t => t + 1)}>Try again</button>
        </div>
      </div>
    );
  }

  // The entry effect, replayed per page (keyed by path; a filter or a preview
  // switch on the same page does not replay it).
  const effect = lastTransition.enabled && lastTransition.effect !== 'none' && page ? lastTransition.effect : '';
  return (
    <PublicShell signedIn={signedIn} onOutbound={outbound}>
      {effect ? (
        <div key={current?.path || path} className={`tpg-enter-${effect}`}
             style={{ animationDuration: `${Math.max(0, lastTransition.duration_ms)}ms` }}>
          {body}
        </div>
      ) : body}
    </PublicShell>
  );
};

export default PageResolver;
